import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Server, ChevronLeft, CheckCircle } from 'lucide-react';

const SubProcessorsPage = () => {
  const processors = [
    {
      name: 'Auth0 / Okta',
      purpose: 'Authentication and identity management',
      dataCategories: 'Email, user ID, access tokens',
      location: 'United States',
      transferMechanism: 'EU-US Data Privacy Framework (Okta certified) and SCCs Module 2',
      dpaHref: 'https://www.okta.com/agreements/',
      scope: 'app + landing',
    },
    {
      name: 'Stripe',
      purpose: 'Billing, subscription management, and fraud prevention',
      dataCategories: 'Email, limited billing details, transaction IDs',
      location: 'United States',
      transferMechanism: 'SCCs Module 2 (and DPF where certified)',
      dpaHref: 'https://stripe.com/legal/dpa',
      scope: 'app + landing checkout flow',
    },
    {
      name: 'OpenAI',
      purpose: 'LLM inference for AI sorting, entity extraction, and chat',
      dataCategories: 'Filenames, folder paths, prompts, and (only with content sorting enabled) extracted text',
      location: 'United States',
      transferMechanism: 'SCCs Module 2',
      dpaHref: 'https://openai.com/policies/data-processing-addendum',
      scope: 'app',
    },
    {
      name: 'Anthropic',
      purpose: 'LLM inference when selected via Bring-Your-Own-Key',
      dataCategories: 'Filenames, folder paths, prompts, and (only with content sorting enabled) extracted text',
      location: 'United States',
      transferMechanism: 'SCCs Module 2',
      dpaHref: 'https://www.anthropic.com/legal/dpa',
      scope: 'app',
      optional: true,
    },
    {
      name: 'Postmark',
      purpose: 'Transactional email delivery',
      dataCategories: 'Email address, message content',
      location: 'United States',
      transferMechanism: 'SCCs Module 2',
      dpaHref: 'https://postmarkapp.com/eu-privacy',
      scope: 'app',
    },
    {
      name: 'Google Cloud SQL (database)',
      purpose: 'Managed Postgres for application data',
      dataCategories: 'Account data, sorting metadata, classifier prompts (30-day window)',
      location: 'United States (us-east1)',
      transferMechanism: 'EU-US Data Privacy Framework (Google certified) and SCCs Module 2',
      dpaHref: 'https://cloud.google.com/terms/data-processing-addendum',
      scope: 'app',
    },
    {
      name: 'Google Cloud Run (compute)',
      purpose: 'Compute hosting for Sortio API services',
      dataCategories: 'All service-side data processed by our API',
      location: 'United States (us-east1)',
      transferMechanism: 'EU-US Data Privacy Framework (Google certified) and SCCs Module 2',
      dpaHref: 'https://cloud.google.com/terms/data-processing-addendum',
      scope: 'app',
    },
    {
      name: 'Cloudinary',
      purpose: 'Media hosting and delivery (testimonials, marketing imagery)',
      dataCategories: 'Uploaded media assets',
      location: 'United States',
      transferMechanism: 'SCCs Module 2',
      dpaHref: 'https://cloudinary.com/privacy',
      scope: 'landing',
      conditional: true,
    },
    {
      name: 'Microsoft Clarity',
      purpose: 'Session analytics (clicks, scrolls, replays) on the marketing site',
      dataCategories: 'Anonymized pointer events, page-render snapshots; loads only with analytics consent',
      location: 'United States',
      transferMechanism: 'EU-US Data Privacy Framework (Microsoft certified) and SCCs Module 2',
      dpaHref: 'https://privacy.microsoft.com/privacystatement',
      scope: 'landing',
    },
    {
      name: 'Google Analytics 4',
      purpose: 'Web analytics on the marketing site',
      dataCategories: 'Page views, IP address (truncated), device and browser metadata; loads only with analytics consent',
      location: 'United States',
      transferMechanism: 'EU-US Data Privacy Framework (Google certified) and SCCs Module 2',
      dpaHref: 'https://business.safety.google/adsprocessorterms/',
      scope: 'landing',
    },
    {
      name: 'Vercel',
      purpose: 'Landing site hosting plus Analytics and Speed Insights',
      dataCategories: 'Edge logs, page views, Core Web Vitals; analytics products load only with analytics consent',
      location: 'United States (Edge presence worldwide)',
      transferMechanism: 'SCCs Module 2',
      dpaHref: 'https://vercel.com/legal/dpa',
      scope: 'landing',
    },
    {
      name: 'Rewardful',
      purpose: 'Affiliate referral tracking',
      dataCategories: 'Referral codes, anonymized click identifiers; loads only with marketing consent',
      location: 'United States',
      transferMechanism: 'SCCs Module 2',
      dpaHref: 'https://www.rewardful.com/privacy',
      scope: 'landing + checkout',
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Sub-processors - Sortio</title>
        <meta name="description" content="List of sub-processors used by Sortio by CMG Labs, with data categories, location, transfer mechanism, and DPA link for each." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Server className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Sub-<span className="apple-gradient-text">processors</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-xl">
            Service providers acting on our instructions under written data protection terms.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-8">
            <p className="text-sm text-apple-400"><em>Last updated: 16 May 2026</em></p>

            <div className="space-y-6">
              <p className="text-apple-300 leading-relaxed">
                The processors below act under our instructions and under written data-protection terms. "Scope" indicates whether the processor handles the desktop app, the marketing site, or both.
              </p>

              <div className="space-y-4">
                {processors.map((p, index) => (
                  <div key={index} className="p-4 rounded-lg bg-apple-700/20 border border-apple-600/20">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-apple-50 font-medium">{p.name}</h3>
                          {p.optional && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-sortio-primary/20 text-sortio-primary border border-sortio-primary/30">
                              Optional (BYOK)
                            </span>
                          )}
                          {p.conditional && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-apple-600/20 text-apple-300 border border-apple-600/30">
                              If used
                            </span>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full bg-apple-600/20 text-apple-300 border border-apple-600/30">
                            {p.scope}
                          </span>
                        </div>
                        <p className="text-apple-300 text-sm mb-1">{p.purpose}</p>
                        <p className="text-apple-400 text-xs"><strong>Data:</strong> {p.dataCategories}</p>
                        <p className="text-apple-400 text-xs"><strong>Location:</strong> {p.location}</p>
                        <p className="text-apple-400 text-xs"><strong>Transfer mechanism:</strong> {p.transferMechanism}</p>
                        {p.dpaHref && (
                          <p className="text-xs mt-1">
                            <a href={p.dpaHref} target="_blank" rel="noopener noreferrer" className="text-sortio-primary hover:text-sortio-primary/80">
                              DPA / Privacy terms
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-lg bg-sortio-secondary/10 border border-sortio-secondary/30 mt-8">
                <p className="text-apple-200 text-sm">
                  <strong>International transfers.</strong> For transfers of EEA, Swiss, or UK personal data to the United States or other third countries, we rely on the EU-US Data Privacy Framework (and its UK and Swiss extensions) where the processor is certified, and Standard Contractual Clauses (Module 2, Controller to Processor) in all other cases, with supplementary measures described in our <Link to="/dpa" className="text-sortio-primary hover:text-sortio-primary/80">DPA</Link>.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-sortio-primary/10 border border-sortio-primary/30">
                <p className="text-apple-200 text-sm">
                  <strong>Notification of changes.</strong> We give at least 30 days' notice before adding or replacing a sub-processor by updating this page. Subscribe to changes by emailing <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">marcus@getsortio.com</a>.
                </p>
              </div>

              <div className="mt-8 text-sm text-apple-300">
                <p>
                  Questions about a specific processor or our data protection practices? Email{' '}
                  <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">
                    marcus@getsortio.com
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubProcessorsPage;
